import { Injectable, signal, inject, DestroyRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientsService } from '../../../../core/services/clients/clients.service';
import { SocioRow } from '../models/socio-row';
import { formatShareAmountEs } from '../utils/format-share-amount';
import { formatPct4 } from '../utils/socios-display.util';
import { formatTituloLabel } from '../utils/titulo-label.util';
import { reconcileShareNumberingsWithCapital } from '../utils/find-series-in-capital.util';
import { ConstitutionCapitalStore } from './constitution-capital.store';

export interface OperationShareNumberingPayload {
  id?: number;
  seriesId: number | null;
  seriesName?: string;
  sharesCount: number;
  sharesNumberFrom: number;
  sharesNumberTo: number;
  titulo: string;
  ownershipPercentage: number | null;
}

export interface OperationSocioPayload {
  id?: number;
  associatedClient?: number | null;
  name: string;
  documentNumber: string;
  address: string;
  representatives?: Array<{ id: number }>;
  shareNumberings: OperationShareNumberingPayload[];
  isWorkerPartner: boolean;
  socialSecurityRegime: string;
  classification: string;
  selfEmployedChargedTo: string;
  selfEmployedChargedName: string;
  bankAccount: number | null;
  monthlyNetIncome: number | null;
  mutualAccidents: string;
  mutualCommonContingencies: string;
  isGeneralMeetingPresident: boolean;
  isGeneralMeetingSecretary: boolean;
  observations: string;
}

type ShareNumberingRow = NonNullable<SocioRow['shareNumberings']>[number];

@Injectable()
export class ConstitutionShareholdersStore {
  private readonly clientsService = inject(ClientsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly capitalStore = inject(ConstitutionCapitalStore, { optional: true });

  readonly socioForm: FormGroup = this.fb.nonNullable.group({
    clientId: this.fb.control<number | null>(null),
    associatedClientId: this.fb.control<number | null>(null),
    name: this.fb.nonNullable.control<string>(''),
    documentNumber: this.fb.nonNullable.control<string>(''),
    address: this.fb.nonNullable.control<string>(''),
    representativeId: this.fb.control<number | null>(null),
    representativeNif: this.fb.nonNullable.control<string>(''),
    representatives: this.fb.nonNullable.control<Array<{ id: number; name: string; nif?: string }>>([]),
    isWorkerPartner: this.fb.nonNullable.control<boolean>(false),
    socialSecurityRegime: this.fb.nonNullable.control<string>(''),
    classification: this.fb.nonNullable.control<string>(''),
    selfEmployedChargedTo: this.fb.nonNullable.control<string>(''),
    selfEmployedChargedName: this.fb.nonNullable.control<string>(''),
    bankAccount: this.fb.control<number | null>(null),
    monthlyNetIncome: this.fb.control<number | null>(null),
    mutualAccidents: this.fb.nonNullable.control<string>(''),
    mutualCommonContingencies: this.fb.nonNullable.control<string>(''),
    isGeneralMeetingPresident: this.fb.nonNullable.control<boolean>(false),
    isGeneralMeetingSecretary: this.fb.nonNullable.control<boolean>(false),
    observations: this.fb.nonNullable.control<string>('')
  });

  /** Control de selección de serie para el modal de numeración. */
  readonly numeracionSeriesControl = this.fb.control<number | null>(null);
  /** Array dinámico de rangos dentro del modal de numeración. */
  readonly numeracionRangesArray = this.fb.array<FormGroup>([]);

  /** Índices de shareNumberings que se están editando (null = nueva entrada). */
  private editingGroupIndices: number[] | null = null;

  readonly socios = signal<SocioRow[]>([]);
  readonly configSociosTable = signal<any | null>(null);
  readonly isSocioModalVisible = signal<boolean>(false);
  readonly isNumeracionModalVisible = signal<boolean>(false);
  readonly numeracionError = signal<string | null>(null);
  readonly clients = signal<Array<{ id: number; name: string; nif?: string }>>([]);
  /** El cliente elegido aporta NIF: en ese caso el campo es de solo lectura; si no, se puede escribir. */
  readonly clientHasNif = signal(false);
  readonly bankAccounts = signal<Array<{ id: number; iban: string; endDate?: string | null }>>([]);
  readonly representativeOptions = signal<Array<{ id: number; name: string; nif?: string }>>([]);
  readonly selectedRepresentativeId = signal<number | null>(null);
  readonly representatives = signal<Array<{ id: number; name: string; nif?: string }>>([]);
  readonly shareNumberings = signal<Array<{ seriesId: number | null; seriesName?: string; sharesCount: number; sharesNumberFrom: number; sharesNumberTo: number; titulo?: string; ownershipPercentage?: number | null }>>([]);

  editingSocioIndex: number | undefined;
  nextSocioId = 1;
  currentClientId: number | null = null;

  /** DB ids loaded from API — distinguish real partner/share ids from local temp ids in wizard */
  private readonly loadedDbPartnerIds = signal<Set<number>>(new Set());
  private readonly loadedDbShareIds = signal<Set<number>>(new Set());

  isDbPartnerId(id: number | null | undefined): boolean {
    if (id == null || id <= 0) return false;
    return this.loadedDbPartnerIds().has(id);
  }

  isDbShareId(id: number | null | undefined): boolean {
    if (id == null || id <= 0) return false;
    return this.loadedDbShareIds().has(id);
  }

  /** Misma serie aunque el id venga como number o string (p. ej. API). */
  private sameSeriesId(
    a: number | string | null | undefined,
    b: number | string | null | undefined
  ): boolean {
    const norm = (x: number | string | null | undefined): number | null => {
      if (x == null || x === '') return null;
      const n = Number(x);
      return Number.isNaN(n) ? null : n;
    };
    return norm(a) === norm(b);
  }

  private isUsufructoTitulo(titulo: string | undefined | null): boolean {
    return (titulo ?? '').trim().toLowerCase() === 'usufructo';
  }

  init(): void {
    this.socioForm.get('associatedClientId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => {
        const selected = this.clients().find(c => c.id === id);
        if (selected) {
          // Always update name when client is selected
          this.socioForm.get('name')?.setValue(selected.name);

          const doc = (selected as any).documentNumber || selected.nif;
          this.clientHasNif.set(!!doc);
          if (doc) this.socioForm.get('documentNumber')?.setValue(doc);
          // Al cambiar a un cliente sin NIF no se arrastra el del anterior (al editar se conserva el del socio).
          else if (this.editingSocioIndex === undefined) this.socioForm.get('documentNumber')?.setValue('');

          if (id) {
            void this.loadBankAccountsForClient(id);
            void this.autofillAddressForClient(id);
            void this.loadRepresentativeOptionsForClient(id);
          }
        } else {
          this.clientHasNif.set(false);
        }
        this.clearRepresentatives();
      });

    this.socioForm.get('representativeId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => {
        this.selectedRepresentativeId.set(id ?? null);
        const selected = this.representativeOptions().find(r => r.id === id);
        this.socioForm.get('representativeNif')?.setValue(selected?.nif || '');
      });

    this.buildSociosTableConfig();
    void this.loadClients();
  }

  private async loadClients(): Promise<void> {
    try {
      this.clients.set(await this.clientsService.getClientsNames());
    } catch {
      this.clients.set([]);
    }
  }

  private async loadRepresentativeOptionsForClient(clientId: number): Promise<void> {
    try {
      const client = await this.clientsService.getClientById(clientId);
      const personType = client?.personType;

      if (personType === 'JURIDIC_PERSON') {
        const administration = await this.clientsService.getAdministrationByClient(clientId);
        const adminId = administration?.id || administration?.administration?.id;
        if (!adminId) {
          this.representativeOptions.set([]);
          return;
        }
        const members = await this.clientsService.getMembersByAdministration(adminId);
        const options = (members || []).map((m: any, index: number) => ({
          id: m?.id ?? m?.memberId ?? index + 1,
          name: m?.name || '',
          nif: m?.documentNumber || m?.nif || '',
          associatedClient: m?.associatedClient?.id ?? m?.associatedClient ?? null
        })).filter((m: any) => m.name);
        this.representativeOptions.set(options);
        return;
      }

      if (personType === 'PHYSICAL_PERSON') {
        const representativeId = client?.representativeId ?? client?.representative?.id ?? client?.representative;
        if (!representativeId) {
          this.representativeOptions.set([]);
          return;
        }
        const representativeClient = await this.clientsService.getClientById(representativeId);
        const options = representativeClient ? [
          {
            id: representativeClient.id,
            name: `${representativeClient.firstSurname || ''} ${representativeClient.secondSurname || ''}, ${representativeClient.name}`.trim() || '',
            nif: representativeClient.documentNumber || representativeClient.nif || '',
            associatedClient: representativeClient.id
          }
        ] : [];
        this.representativeOptions.set(options.filter((p: any) => p.name));
        return;
      }

      this.representativeOptions.set([]);
    } catch {
      this.representativeOptions.set([]);
    }
  }

  async loadShareholdersData(clientId: number): Promise<void> {
    try {
      this.currentClientId = clientId;
      const shareholdersRes = await this.clientsService.getClientShareholders(clientId);
      const partners = Array.isArray(shareholdersRes) ? shareholdersRes : (shareholdersRes?.shareholders || []);

      if (partners.length > 0) {
        const classificationMapReverse: any = { GENERAL: 'general', SELF_EMPLOYED: 'autonomos' };
        const chargeMapReverse: any = { COMPANY: 'empresa', PERSON: 'persona' };

        const mapClientToRepresentative = (r: any) => {
          const id = r?.id ?? (typeof r === 'number' ? r : null);
          if (id == null) return null;
          const first = r?.firstSurname ?? r?.first_surname ?? '';
          const second = r?.secondSurname ?? r?.second_surname ?? '';
          const given = r?.name ?? '';
          const fullName = [first, second, given].filter(Boolean).join(' ').trim() || given || '';
          const nif = r?.documentNumber ?? r?.document_number ?? r?.nif ?? '';
          return { id, name: fullName, nif, associatedClient: id };
        };

        const sociosPromises = partners.map(async (p: any, index: number) => {
          const associatedClient = p.associatedClient?.id
            ? p.associatedClient
            : (p.associatedClientId ? { id: p.associatedClientId } : null);

          // Representatives come from client_partner_representatives (partner.representatives in API)
          let representatives: Array<{ id: number; name: string; nif?: string; associatedClient?: number }> = [];
          if (Array.isArray(p?.representatives) && p.representatives.length > 0) {
            representatives = p.representatives
              .map((r: any) => mapClientToRepresentative(r))
              .filter((x): x is { id: number; name: string; nif?: string; associatedClient?: number } => x != null && (x.name || x.id));
          }

          const representative = p?.representative || null;
          const firstRep = representatives[0];
          const representativeName =
            firstRep?.name ||
            (representative
              ? [representative.firstSurname, representative.secondSurname, representative.name]
                .filter(Boolean)
                .join(' ')
                .trim()
              : '') ||
            (p?.representativeName || '');
          const representativeNif =
            firstRep?.nif ||
            representative?.documentNumber ||
            representative?.document_number ||
            representative?.nif ||
            p?.representativeNif ||
            '';

          if (representatives.length === 0 && representative) {
            representatives = [{
              id: representative.id,
              name: representativeName,
              nif: representativeNif,
              associatedClient: representative.id
            }];
          }

          // Handle share numberings (mapped from partnerShares or shareNumberings)
          const rawShareNumberings = Array.isArray(p?.partnerShares) ? p.partnerShares : (Array.isArray(p?.shareNumberings) ? p.shareNumberings : []);

          const shareNumberings = rawShareNumberings.length > 0
            ? rawShareNumberings.map((sn: any) => ({
              id: sn.id ?? null,
              seriesId: sn.seriesId ?? sn.series?.id ?? sn.socialCapital?.id ?? null,
              seriesName: sn.seriesName || sn.series?.name || sn.socialCapital?.name || '',
              sharesCount: sn.sharesCount ?? sn.sharesNum,
              sharesNumberFrom: sn.sharesNumberFrom ?? sn.shareFrom,
              sharesNumberTo: sn.sharesNumberTo ?? sn.shareTo,
              titulo: sn.titulo || '',
              ownershipPercentage: toNumberOrNull(sn.ownershipPercentage),
              nominalValue: toNumberOrNull(sn.nominalValue ?? sn.socialCapital?.nominalValue),
            }))
            : [];

          const totalShares = shareNumberings.length > 0
            ? shareNumberings.reduce((sum: number, sn: any) => sum + sn.sharesCount, 0)
            : (p.sharesCount || 0);

          return {
            id: p.id || index + 1,
            associatedClient,
            name: p.name,
            documentNumber: p.documentNumber,
            address: p.address,
            representatives,
            representativeName: representativeName,
            representativeNif: representativeNif,
            shareNumberings,
            seriesId: p.seriesId,
            sharesCount: totalShares,
            sharesNumberFrom: p.sharesNumberFrom,
            sharesNumberTo: p.sharesNumberTo,
            isWorkerPartner: p.isWorkerPartner,
            socialSecurityRegime: p.socialSecurityRegime,
            classification: classificationMapReverse[p.classification] ?? p.classification,
            selfEmployedChargedTo: chargeMapReverse[p.selfEmployedChargedTo] ?? p.selfEmployedChargedTo,
            selfEmployedChargedName: p.selfEmployedChargedName,
            bankAccount: p.bankAccount?.id ?? (typeof p.bankAccount === 'number' ? p.bankAccount : null),
            monthlyNetIncome: p.monthlyNetIncome,
            mutualAccidents: p.mutualAccidents,
            mutualCommonContingencies: p.mutualCommonContingencies,
            isGeneralMeetingPresident: p.isGeneralMeetingPresident,
            isGeneralMeetingSecretary: p.isGeneralMeetingSecretary,
            observations: p.observations ?? ''
          };
        });
        const socios = await Promise.all(sociosPromises);
        this.capitalStore?.mergeReferencedSeries(collectReferencedSeries(partners));
        const partnerIds = new Set<number>();
        const shareIds = new Set<number>();
        for (const s of socios) {
          if (typeof s.id === 'number' && s.id > 0 && partners.some((p: any) => p.id === s.id)) {
            partnerIds.add(s.id);
          }
          for (const sn of s.shareNumberings ?? []) {
            if (sn.id != null && sn.id > 0) shareIds.add(sn.id);
          }
        }
        this.loadedDbPartnerIds.set(partnerIds);
        this.loadedDbShareIds.set(shareIds);
        this.socios.set(socios);
        const maxId = socios.reduce((max, s) => Math.max(max, s.id), 0);
        this.nextSocioId = maxId + 1;
        this.reconcileAllSociosWithCapital();
      } else {
        this.loadedDbPartnerIds.set(new Set());
        this.loadedDbShareIds.set(new Set());
      }
    } catch (error) {
      console.error('Error loading Shareholders data:', error);
    }
  }

  private resolveSeriesName(seriesId: number | null, capitalStore?: ConstitutionCapitalStore): string {
    if (seriesId == null || !capitalStore) return '';
    const match = capitalStore.series().find(
      s => s.id === seriesId || s.feLocalId === seriesId,
    );
    return match?.name ?? '';
  }

  reconcileShareNumberingsArray(
    shareNumberings: SocioRow['shareNumberings'],
    capitalStore?: ConstitutionCapitalStore,
  ): ShareNumberingRow[] {
    const store = capitalStore ?? this.capitalStore;
    if (!store) {
      return (shareNumberings ?? []).map(sn => ({ ...sn }));
    }
    return reconcileShareNumberingsWithCapital(
      shareNumberings ?? [],
      store.series(),
      store.hasSeries(),
    );
  }

  reconcileAllSociosWithCapital(capitalStore?: ConstitutionCapitalStore): void {
    const store = capitalStore ?? this.capitalStore;
    if (!store) return;
    this.socios.update(list =>
      list.map(s => ({
        ...s,
        shareNumberings: this.reconcileShareNumberingsArray(s.shareNumberings, store),
      })),
    );
    this.buildSociosTableConfig();
  }

  buildShareNumberingsPayload(
    shareNumberings: SocioRow['shareNumberings'],
    capitalStore?: ConstitutionCapitalStore,
  ): OperationShareNumberingPayload[] {
    const reconciled = this.reconcileShareNumberingsArray(shareNumberings, capitalStore);
    return reconciled.map(sn => {
      const payload: OperationShareNumberingPayload = {
        seriesId: sn.seriesId,
        seriesName: sn.seriesName ?? this.resolveSeriesName(sn.seriesId, capitalStore ?? this.capitalStore),
        sharesCount: sn.sharesCount,
        sharesNumberFrom: sn.sharesNumberFrom,
        sharesNumberTo: sn.sharesNumberTo,
        titulo: sn.titulo ?? '',
        ownershipPercentage: sn.ownershipPercentage ?? null,
      };
      if (sn.id != null && this.isDbShareId(sn.id)) {
        payload.id = sn.id;
      }
      return payload;
    });
  }

  serializeShareNumberingsForSocio(
    shareNumberings: SocioRow['shareNumberings'],
    capitalStore?: ConstitutionCapitalStore,
  ): OperationShareNumberingPayload[] {
    return this.buildShareNumberingsPayload(shareNumberings, capitalStore);
  }

  serializeSocioForOperation(socio: SocioRow, capitalStore?: ConstitutionCapitalStore): OperationSocioPayload {
    const classificationMap: Record<string, string> = { general: 'GENERAL', autonomos: 'SELF_EMPLOYED' };
    const chargeMap: Record<string, string> = { empresa: 'COMPANY', persona: 'PERSON' };
    const reps = (socio.representatives ?? []).map(r => ({ id: (r as any).associatedClient ?? r.id }));

    const payload: OperationSocioPayload = {
      associatedClient: socio.associatedClient?.id ?? null,
      name: socio.name,
      documentNumber: socio.documentNumber,
      address: socio.address,
      representatives: reps.length > 0 ? reps : undefined,
      shareNumberings: this.serializeShareNumberingsForSocio(socio.shareNumberings, capitalStore),
      isWorkerPartner: socio.isWorkerPartner,
      socialSecurityRegime: socio.socialSecurityRegime,
      classification: classificationMap[socio.classification] ?? socio.classification,
      selfEmployedChargedTo: chargeMap[socio.selfEmployedChargedTo] ?? socio.selfEmployedChargedTo,
      selfEmployedChargedName: socio.selfEmployedChargedName,
      bankAccount: socio.bankAccount,
      monthlyNetIncome: socio.monthlyNetIncome,
      mutualAccidents: socio.mutualAccidents,
      mutualCommonContingencies: socio.mutualCommonContingencies,
      isGeneralMeetingPresident: socio.isGeneralMeetingPresident,
      isGeneralMeetingSecretary: socio.isGeneralMeetingSecretary,
      observations: socio.observations ?? '',
    };
    if (this.isDbPartnerId(socio.id)) {
      payload.id = socio.id;
    }
    return payload;
  }

  serializeSociosForOperation(capitalStore?: ConstitutionCapitalStore): OperationSocioPayload[] {
    const store = capitalStore ?? this.capitalStore;
    return this.socios().map(s => {
      const reconciledSocio = store
        ? { ...s, shareNumberings: this.reconcileShareNumberingsArray(s.shareNumberings, store) }
        : s;
      return this.serializeSocioForOperation(reconciledSocio, store);
    });
  }

  hydrateFromOperation(
    socios: OperationSocioPayload[] | null | undefined,
    capitalStore?: ConstitutionCapitalStore,
  ): void {
    if (!socios?.length) return;

    const classificationMapReverse: Record<string, string> = { GENERAL: 'general', SELF_EMPLOYED: 'autonomos' };
    const chargeMapReverse: Record<string, string> = { COMPANY: 'empresa', PERSON: 'persona' };

    const dbPartnersById = new Map(
      this.socios().filter(s => this.isDbPartnerId(s.id)).map(s => [s.id, s]),
    );
    const dbPartnersByDoc = new Map(
      this.socios()
        .filter(s => this.isDbPartnerId(s.id) && s.documentNumber)
        .map(s => [s.documentNumber.trim().toLowerCase(), s]),
    );

    const shareIdsFromDb = new Set(this.loadedDbShareIds());

    const reconciled: SocioRow[] = socios.map(opSocio => {
      let dbPartner: SocioRow | undefined;
      if (opSocio.id != null && dbPartnersById.has(opSocio.id)) {
        dbPartner = dbPartnersById.get(opSocio.id);
      } else if (opSocio.documentNumber) {
        dbPartner = dbPartnersByDoc.get(opSocio.documentNumber.trim().toLowerCase());
      }

      const localId = dbPartner?.id ?? this.nextSocioId++;
      if (!dbPartner) {
        this.nextSocioId = Math.max(this.nextSocioId, localId + 1);
      }

      const partnerIsDb =
        opSocio.id != null &&
        opSocio.id > 0 &&
        (this.isDbPartnerId(opSocio.id) || dbPartner != null);
      if (opSocio.id != null && opSocio.id > 0) {
        this.loadedDbPartnerIds.update(ids => new Set([...ids, opSocio.id!]));
      }

      const shareNumberings = this.reconcileShareNumberingsArray(
        (opSocio.shareNumberings ?? []).map(sn => {
          const seriesId = sn.seriesId;
          let seriesName = sn.seriesName ?? '';
          if (!seriesName && seriesId != null && capitalStore) {
            seriesName = this.resolveSeriesName(seriesId, capitalStore);
          }
          const row: ShareNumberingRow = {
            seriesId,
            seriesName,
            sharesCount: sn.sharesCount,
            sharesNumberFrom: sn.sharesNumberFrom,
            sharesNumberTo: sn.sharesNumberTo,
            titulo: sn.titulo ?? '',
            ownershipPercentage: toNumberOrNull(sn.ownershipPercentage),
          };
          if (sn.id != null && sn.id > 0 && (partnerIsDb || this.isDbShareId(sn.id))) {
            row.id = sn.id;
            shareIdsFromDb.add(sn.id);
          }
          return row;
        }),
        capitalStore,
      );

      const totalShares = shareNumberings.reduce((sum, sn) => sum + (sn.sharesCount ?? 0), 0);

      return {
        id: localId,
        associatedClient: opSocio.associatedClient != null ? { id: opSocio.associatedClient } : null,
        name: opSocio.name,
        documentNumber: opSocio.documentNumber,
        address: opSocio.address,
        representatives: dbPartner?.representatives ?? [],
        representativeName: dbPartner?.representativeName ?? '',
        representativeNif: dbPartner?.representativeNif ?? '',
        shareNumberings,
        sharesCount: totalShares,
        seriesId: null,
        sharesNumberFrom: null,
        sharesNumberTo: null,
        isWorkerPartner: opSocio.isWorkerPartner,
        socialSecurityRegime: opSocio.socialSecurityRegime,
        classification: classificationMapReverse[opSocio.classification] ?? opSocio.classification,
        selfEmployedChargedTo: chargeMapReverse[opSocio.selfEmployedChargedTo] ?? opSocio.selfEmployedChargedTo,
        selfEmployedChargedName: opSocio.selfEmployedChargedName,
        bankAccount: opSocio.bankAccount,
        monthlyNetIncome: opSocio.monthlyNetIncome,
        mutualAccidents: opSocio.mutualAccidents,
        mutualCommonContingencies: opSocio.mutualCommonContingencies,
        isGeneralMeetingPresident: opSocio.isGeneralMeetingPresident,
        isGeneralMeetingSecretary: opSocio.isGeneralMeetingSecretary,
        observations: opSocio.observations ?? '',
      };
    });

    this.loadedDbShareIds.set(shareIdsFromDb);
    this.socios.set(reconciled);
    this.buildSociosTableConfig();
  }

  private syncShareNumberingsToEditingSocio(): void {
    if (this.editingSocioIndex === undefined) return;
    const idx = this.editingSocioIndex;
    const capitalStore = this.capitalStore;
    const reconciled = capitalStore
      ? this.reconcileShareNumberingsArray(this.shareNumberings(), capitalStore)
      : [...this.shareNumberings()];
    this.shareNumberings.set(reconciled);
    const totalShares = reconciled.reduce((sum, n) => sum + (n.sharesCount ?? 0), 0);
    this.socios.update(list =>
      list.map((s, i) =>
        i === idx ? { ...s, shareNumberings: reconciled, sharesCount: totalShares } : s,
      ),
    );
    this.buildSociosTableConfig();
  }

  buildSociosTableConfig(): void {
    const data = this.socios().map(s => {
      const numberings: any[] = (s as any).shareNumberings || [];

      const uniqueTitulos = [...new Set(
        numberings.map(n => n.titulo).filter(Boolean)
      )].map(t => formatTituloLabel(t));

      const nonUsufructoNumberings = numberings.filter(n => !this.isUsufructoTitulo(n.titulo));

      // Nº Acciones: solo numeraciones no usufructo (suma bruta)
      const sharesCountNoUsuf = nonUsufructoNumberings
        .reduce((sum, n) => sum + (n.sharesCount ?? 0), 0);

      // Nº Acciones Ajustado: solo no usufructo × % titularidad
      const sharesCountAdjusted = nonUsufructoNumberings
        .reduce((sum, n) => {
          const pct = Number(n.ownershipPercentage ?? 0) / 100;
          return sum + (n.sharesCount ?? 0) * pct;
        }, 0);

      return {
        ...s,
        sharesCount: sharesCountNoUsuf,
        sharesCountDisplay: formatShareAmountEs(sharesCountNoUsuf),
        titulosDisplay: uniqueTitulos.join(', ') || '-',
        sharesCountAdjusted,
        sharesCountAdjustedDisplay: formatShareAmountEs(sharesCountAdjusted),
        trabajadorIcon: s.isWorkerPartner ? '✓' : '',
        presidenteIcon: s.isGeneralMeetingPresident ? '✓' : '',
        secretarioIcon: s.isGeneralMeetingSecretary ? '✓' : '',
        observations: (s as any).observations ?? ''
      };
    });

    this.configSociosTable.set({
      headers: [
        { name: 'Nombre', key: 'name' },
        { name: 'NIF', key: 'documentNumber' },
        { name: 'Nº Acciones', key: 'sharesCountDisplay', textAlign: 'right' },
        { name: 'Nº Acc. ajustado', key: 'sharesCountAdjustedDisplay', textAlign: 'right' },
        { name: 'Título', key: 'titulosDisplay' },
        { name: 'Socio Trabajador', key: 'trabajadorIcon', textAlign: 'center' },
        { name: 'Presidente', key: 'presidenteIcon', textAlign: 'center' },
        { name: 'Secretario', key: 'secretarioIcon', textAlign: 'center' },
        { name: 'Observaciones', key: 'observations' },
        { name: 'Acciones', key: 'delete' }
      ],
      data,
      isPersons: true
    });
  }

  openSocioModal(index?: number): void {
    if (index === undefined) {
      this.editingSocioIndex = undefined;
      this.socioForm.reset({
        clientId: null,
        associatedClientId: null,
        name: '',
        documentNumber: '',
        address: '',
        representativeId: null,
        representativeNif: '',
        representatives: [],
        isWorkerPartner: false,
        socialSecurityRegime: '',
        classification: '',
        selfEmployedChargedTo: '',
        selfEmployedChargedName: '',
        bankAccount: null,
        monthlyNetIncome: null,
        mutualAccidents: '',
        mutualCommonContingencies: '',
        isGeneralMeetingPresident: false,
        isGeneralMeetingSecretary: false,
        observations: ''
      });
      this.shareNumberings.set([]);
    } else {
      this.editingSocioIndex = index;
      const s = this.socios()[index];

      // Extract and normalize representatives
      const repsData = (s as any).representatives || [];

      const shareNumberingsData = this.reconcileShareNumberingsArray(
        (s as any).shareNumberings || [],
        this.capitalStore,
      );

      this.socioForm.patchValue({
        clientId: null,
        associatedClientId: s.associatedClient?.id || null,
        name: s.name,
        documentNumber: s.documentNumber,
        address: s.address,
        representativeId: null,
        representativeNif: '',
        representatives: repsData,
        isWorkerPartner: s.isWorkerPartner,
        socialSecurityRegime: s.socialSecurityRegime,
        classification: s.classification,
        selfEmployedChargedTo: s.selfEmployedChargedTo,
        selfEmployedChargedName: s.selfEmployedChargedName,
        bankAccount: s.bankAccount,
        monthlyNetIncome: s.monthlyNetIncome,
        mutualAccidents: s.mutualAccidents,
        mutualCommonContingencies: s.mutualCommonContingencies,
        isGeneralMeetingPresident: s.isGeneralMeetingPresident,
        isGeneralMeetingSecretary: s.isGeneralMeetingSecretary,
        observations: (s as any).observations ?? ''
      });

      // Set representatives signal for table display
      this.representatives.set(repsData);

      // Set share numberings signal for table display
      this.shareNumberings.set(shareNumberingsData);
    }
    if (index === undefined) {
      this.representatives.set([]);
    }
    this.isSocioModalVisible.set(true);
  }

  closeSocioModal(): void {
    this.isSocioModalVisible.set(false);
    this.shareNumberings.set([]);
    this.representatives.set([]);
    this.editingSocioIndex = undefined;
  }

  addRepresentative(): void {
    const id = this.selectedRepresentativeId();
    if (!id) return;
    const selected = this.representativeOptions().find(r => r.id === id);
    if (!selected) return;
    const current = this.representatives();
    if (current.some(r => r.id === id)) return;
    const next = [...current, { ...selected, associatedClient: (selected as any).associatedClient ?? selected.id }];
    this.representatives.set(next);
    this.socioForm.get('representatives')?.setValue(next);
    this.socioForm.get('representativeId')?.setValue(null);
    this.socioForm.get('representativeNif')?.setValue('');
  }

  removeRepresentative(index: number, deferPersistence = false): void {
    const next = this.representatives().filter((_, i) => i !== index);
    this.representatives.set(next);
    this.socioForm.get('representatives')?.setValue(next);

    // If editing an existing partner, update in backend (unless deferred)
    if (this.editingSocioIndex !== undefined) {
      const socio = this.socios()[this.editingSocioIndex];
      if (socio?.id && !deferPersistence && this.isDbPartnerId(socio.id)) {
        // Get current form values
        const v = this.socioForm.getRawValue();

        // Convert representatives to IDs for backend
        const representativeIds = next.map((r: any) => r.associatedClient ?? r.id);

        // Build payload with all data and updated representatives
        const representativesPayload = (representativeIds as number[]).map((id) => ({ id }));
        const classificationMapRep: Record<string, string> = { general: 'GENERAL', autonomos: 'SELF_EMPLOYED' };
        const chargeMapRep: Record<string, string> = { empresa: 'COMPANY', persona: 'PERSON' };
        const shareNumberingsPayload = this.buildShareNumberingsPayload(
          this.shareNumberings().length > 0
            ? this.shareNumberings()
            : socio.shareNumberings,
          this.capitalStore,
        );
        const socioPayload: any = {
          id: socio.id,
          client: this.currentClientId,
          associatedClient: v.associatedClientId ?? null,
          name: v.name,
          documentNumber: v.documentNumber,
          address: v.address,
          representatives: representativesPayload,
          shareNumberings: shareNumberingsPayload,
          isWorkerPartner: v.isWorkerPartner,
          socialSecurityRegime: v.socialSecurityRegime,
          classification: classificationMapRep[v.classification] ?? v.classification,
          selfEmployedChargedTo: chargeMapRep[v.selfEmployedChargedTo] ?? v.selfEmployedChargedTo,
          selfEmployedChargedName: v.selfEmployedChargedName,
          bankAccount: v.bankAccount,
          monthlyNetIncome: v.monthlyNetIncome,
          mutualAccidents: v.mutualAccidents,
          mutualCommonContingencies: v.mutualCommonContingencies,
          isGeneralMeetingPresident: v.isGeneralMeetingPresident,
          isGeneralMeetingSecretary: v.isGeneralMeetingSecretary
        };

        // Save to backend
        this.clientsService.saveClientShareholders(socioPayload);

        // Update local state
        const updatedSocio = {
          ...socio,
          representatives: next
        };
        this.socios.update(list => list.map((s, i) => i === this.editingSocioIndex ? updatedSocio : s));
      }
    }
  }

  private clearRepresentatives(): void {
    this.representatives.set([]);
    this.socioForm.get('representatives')?.setValue([]);
    this.socioForm.get('representativeId')?.setValue(null);
    this.socioForm.get('representativeNif')?.setValue('');
  }

  /** Abre el modal en modo "nueva numeración" (sin serie ni rangos). */
  openNumeracionModalNew(): void {
    this.numeracionError.set(null);
    this.editingGroupIndices = null;
    this.numeracionSeriesControl.setValue(null);
    this.clearRangesArray();
    this.numeracionRangesArray.push(this.createRangeGroup());
    this.isNumeracionModalVisible.set(true);
  }

  /**
   * Abre el modal en modo edición para un grupo de series existente.
   * @param seriesId  id de la serie (null = sin serie)
   * @param rowIndices  índices en shareNumberings() que pertenecen al grupo
   */
  openNumeracionModalForGroup(seriesId: number | null, rowIndices: number[]): void {
    this.numeracionError.set(null);
    this.editingGroupIndices = rowIndices;
    this.numeracionSeriesControl.setValue(seriesId);
    this.clearRangesArray();
    const list = this.shareNumberings();
    if (rowIndices.length === 0) {
      this.numeracionRangesArray.push(this.createRangeGroup());
    } else {
      for (const idx of rowIndices) {
        const n = list[idx];
        this.numeracionRangesArray.push(this.createRangeGroup({
          id: (n as any).id ?? null,
          sharesNumberFrom: n.sharesNumberFrom,
          sharesNumberTo: n.sharesNumberTo,
          sharesCount: n.sharesCount,
          titulo: n.titulo ?? '',
          ownershipPercentage: n.ownershipPercentage ?? null
        }));
      }
    }
    this.isNumeracionModalVisible.set(true);
  }

  closeNumeracionModal(): void {
    this.isNumeracionModalVisible.set(false);
    this.numeracionError.set(null);
    this.editingGroupIndices = null;
  }

  addModalRange(): void {
    this.numeracionRangesArray.push(this.createRangeGroup());
  }

  removeModalRange(index: number): void {
    if (this.numeracionRangesArray.length > 1) {
      this.numeracionRangesArray.removeAt(index);
    }
  }

  saveNumeracionModal(
    seriesOptions: Array<{ value: number; label: string }>,
    seriesTotalsMap: Map<number, number>,
    singleSeriesTotal: number
  ): void {
    if (this.numeracionRangesArray.invalid) return;

    const seriesId = this.numeracionSeriesControl.value ?? null;
    const ranges = this.numeracionRangesArray.getRawValue() as Array<{
      id: number | null;
      sharesNumberFrom: number | null;
      sharesNumberTo: number | null;
      sharesCount: number | null;
      titulo: string;
      ownershipPercentage: number | null;
    }>;
    const seriesName = seriesOptions.find(s => s.value === seriesId)?.label || '';

    // Validar límite de la serie (solo rangos no usufructo)
    const seriesLimit = seriesId !== null
      ? (seriesTotalsMap.get(seriesId) ?? null)
      : (singleSeriesTotal > 0 ? singleSeriesTotal : null);

    if (seriesLimit !== null) {
      const modalNonUsufructoTotal = ranges
        .filter(r => !this.isUsufructoTitulo(r.titulo))
        .reduce((sum, r) => sum + (r.sharesCount ?? 0), 0);

      // Acciones de otras series del socio (las que NO se están editando)
      const editSet = new Set(this.editingGroupIndices ?? []);
      const otherTotal = this.shareNumberings()
        .filter((_, i) => !editSet.has(i))
        .filter(n => this.sameSeriesId(n.seriesId, seriesId) && !this.isUsufructoTitulo(n.titulo))
        .reduce((sum, n) => sum + n.sharesCount, 0);

      if (otherTotal + modalNonUsufructoTotal > seriesLimit) {
        const available = seriesLimit - otherTotal;
        this.numeracionError.set(
          `El total de acciones no usufructo (${formatShareAmountEs(modalNonUsufructoTotal)}) supera el disponible. Máximo: ${formatShareAmountEs(available)} (total serie: ${formatShareAmountEs(seriesLimit)}, ya asignadas en otros grupos: ${formatShareAmountEs(otherTotal)}).`
        );
        return;
      }
    }

    this.numeracionError.set(null);

    const newRanges = ranges.map(r => ({
      ...(r.id != null && this.isDbShareId(r.id) ? { id: r.id } : {}),
      seriesId,
      seriesName,
      sharesCount: r.sharesCount ?? 0,
      sharesNumberFrom: r.sharesNumberFrom!,
      sharesNumberTo: r.sharesNumberTo!,
      titulo: r.titulo || '',
      ownershipPercentage: r.ownershipPercentage ?? null
    }));

    if (this.editingGroupIndices !== null) {
      const removeSet = new Set(this.editingGroupIndices);
      const remaining = this.shareNumberings().filter((_, i) => !removeSet.has(i));
      this.shareNumberings.set([...remaining, ...newRanges]);
    } else {
      this.shareNumberings.update(list => [...list, ...newRanges]);
    }

    this.syncShareNumberingsToEditingSocio();
    this.closeNumeracionModal();
  }

  private clearRangesArray(): void {
    while (this.numeracionRangesArray.length > 0) {
      this.numeracionRangesArray.removeAt(0);
    }
  }

  private createRangeGroup(data?: {
    id?: number | null;
    sharesNumberFrom?: number | null;
    sharesNumberTo?: number | null;
    sharesCount?: number | null;
    titulo?: string;
    ownershipPercentage?: number | null;
  }): FormGroup {
    return this.fb.group({
      id: this.fb.control<number | null>(data?.id ?? null),
      sharesNumberFrom: this.fb.control<number | null>(data?.sharesNumberFrom ?? null),
      sharesNumberTo: this.fb.control<number | null>(data?.sharesNumberTo ?? null),
      sharesCount: this.fb.control<number | null>(data?.sharesCount ?? null),
      titulo: this.fb.nonNullable.control<string>(data?.titulo ?? ''),
      ownershipPercentage: this.fb.control<number | null>(data?.ownershipPercentage ?? null)
    }, { validators: [this.sharesRangeValidator()] });
  }

  removeNumeracion(index: number): void {
    this.shareNumberings.update(list => list.filter((_, i) => i !== index));

  }

  /** Elimina todas las numeraciones de una serie (mismo seriesId, incl. null). */
  removeNumeracionBySeries(seriesId: number | null): void {
    this.shareNumberings.update(list =>
      list.filter(n => !this.sameSeriesId(n.seriesId, seriesId))
    );
  }

  removeNumeracionesAtIndices(indices: number[]): void {
    const drop = new Set(indices);
    this.shareNumberings.update(list => list.filter((_, i) => !drop.has(i)));
  }

  getTotalShares(): number {
    return this.shareNumberings().reduce((sum, n) => sum + n.sharesCount, 0);
  }

  getSharesBySeries(): Array<{ seriesId: number | null; seriesName: string; total: number }> {
    const grouped = new Map<number | null, { seriesName: string; total: number }>();

    this.shareNumberings()
      .filter(n => !this.isUsufructoTitulo(n.titulo))
      .forEach(n => {
        const existing = grouped.get(n.seriesId);
        if (existing) {
          existing.total += n.sharesCount;
        } else {
          grouped.set(n.seriesId, {
            seriesName: n.seriesName || 'Sin serie',
            total: n.sharesCount
          });
        }
      });

    return Array.from(grouped.entries()).map(([seriesId, data]) => ({
      seriesId,
      ...data
    }));
  }

  formatPercent4(value: number, total: number): string {
    if (!total) return '-';
    const pct = (value / total) * 100;
    return formatPct4(pct);
  }

  getSeriesValidationStatus(capitalStore: any): Array<{ seriesId: number | null; seriesName: string; total: number; expected: number | null; isValid: boolean }> {
    return this.getSharesBySeries().map(serieTotal => {
      const expected = serieTotal.seriesId !== null ? (capitalStore.seriesTotalsMap().get(serieTotal.seriesId) ?? null) : null;
      return {
        ...serieTotal,
        expected,
        isValid: expected === null || serieTotal.total === expected
      };
    });
  }

  getAllSociosSeriesValidation(capitalStore: any): Array<{ seriesId: number | null; seriesName: string; total: number; expected: number | null; isValid: boolean }> {
    const grouped = new Map<number | null, { seriesName: string; total: number }>();

    this.socios().forEach(socio => {
      (socio.shareNumberings || [])
        .filter(sn => !this.isUsufructoTitulo(sn.titulo))
        .forEach(sn => {
          const existing = grouped.get(sn.seriesId);
          if (existing) {
            existing.total += sn.sharesCount;
          } else {
            grouped.set(sn.seriesId, { seriesName: sn.seriesName || 'Sin serie', total: sn.sharesCount });
          }
        });
    });

    return Array.from(grouped.entries()).map(([seriesId, data]) => {
      let expected: number | null = null;
      if (seriesId !== null) {
        expected = capitalStore.seriesTotalsMap().get(seriesId) ?? null;
      } else if (!capitalStore.hasSeries()) {
        const totalSharesVal = capitalStore.capitalSocialForm.get('totalShares')?.value;
        expected = totalSharesVal != null ? Number(totalSharesVal) : null;
      }
      return {
        seriesId,
        seriesName: data.seriesName,
        total: data.total,
        expected,
        isValid: expected === null || data.total === expected
      };
    });
  }


  async saveSocio(clientId: number | null, deferPersistence = false): Promise<void> {
    const v = this.socioForm.getRawValue();
    const isEditing = this.editingSocioIndex !== undefined;
    const existingId = isEditing ? this.socios()[this.editingSocioIndex!].id : null;
    const tempId = isEditing ? existingId : this.nextSocioId++;

    // Keep full objects for UI
    const representativesForTable = v.representatives || [];
    // Convert to IDs for backend
    const representativeIds = representativesForTable.map((r: any) => r.associatedClient ?? r.id);
    const firstRepresentative = representativesForTable[0];


    const capitalStore = this.capitalStore;
    const shareNumberingsData = capitalStore
      ? this.reconcileShareNumberingsArray(this.shareNumberings(), capitalStore)
      : [...this.shareNumberings()];
    const totalShares = shareNumberingsData.reduce((sum, n) => sum + (n.sharesCount ?? 0), 0);

    const socioForTable: SocioRow = {
      id: tempId!,
      associatedClient: v.associatedClientId ? { id: v.associatedClientId } : null,
      name: v.name,
      documentNumber: v.documentNumber,
      address: v.address,
      representatives: representativesForTable,
      representativeName: firstRepresentative?.name || '',
      representativeNif: firstRepresentative?.nif || '',
      shareNumberings: shareNumberingsData,
      sharesCount: totalShares,
      seriesId: null,
      sharesNumberFrom: null,
      sharesNumberTo: null,
      isWorkerPartner: v.isWorkerPartner,
      socialSecurityRegime: v.socialSecurityRegime,
      classification: v.classification,
      selfEmployedChargedTo: v.selfEmployedChargedTo,
      selfEmployedChargedName: v.selfEmployedChargedName,
      bankAccount: v.bankAccount,
      monthlyNetIncome: v.monthlyNetIncome,
      mutualAccidents: v.mutualAccidents,
      mutualCommonContingencies: v.mutualCommonContingencies,
      isGeneralMeetingPresident: v.isGeneralMeetingPresident,
      isGeneralMeetingSecretary: v.isGeneralMeetingSecretary,
      observations: v.observations ?? ''
    };

    // Backend expects representatives as array (ids or { id }); send objects so it's explicit
    const representativesPayload = (representativeIds as number[]).map((id) => ({ id }));

    // Use passed clientId or currentClientId if available
    const finalClientId = clientId ?? this.currentClientId;

    const classificationMap: Record<string, string> = { general: 'GENERAL', autonomos: 'SELF_EMPLOYED' };
    const chargeMap: Record<string, string> = { empresa: 'COMPANY', persona: 'PERSON' };

    const socioPayload: any = {
      client: finalClientId,
      associatedClient: v.associatedClientId ?? null,
      name: v.name,
      documentNumber: v.documentNumber,
      address: v.address,
      representatives: representativesPayload,
      shareNumberings: this.buildShareNumberingsPayload(shareNumberingsData, capitalStore),
      isWorkerPartner: v.isWorkerPartner,
      socialSecurityRegime: v.socialSecurityRegime,
      classification: classificationMap[v.classification] ?? v.classification,
      selfEmployedChargedTo: chargeMap[v.selfEmployedChargedTo] ?? v.selfEmployedChargedTo,
      selfEmployedChargedName: v.selfEmployedChargedName,
      bankAccount: v.bankAccount,
      monthlyNetIncome: v.monthlyNetIncome,
      mutualAccidents: v.mutualAccidents,
      mutualCommonContingencies: v.mutualCommonContingencies,
      isGeneralMeetingPresident: v.isGeneralMeetingPresident,
      isGeneralMeetingSecretary: v.isGeneralMeetingSecretary,
      observations: v.observations ?? ''
    };

    if (isEditing) {
      socioPayload.id = existingId;
    }

    // Optimistic update
    if (isEditing) {
      this.socios.update(list => list.map((s, i) => i === this.editingSocioIndex ? socioForTable : s));
    } else {
      this.socios.update(list => [...list, socioForTable]);
    }

    this.buildSociosTableConfig();
    this.isSocioModalVisible.set(false);

    try {
      if (!deferPersistence && finalClientId) {
        const saved = await this.clientsService.saveClientShareholders(socioPayload);

        // Sync real backend ID and partnerShares IDs into local state
        if (saved?.id) {
          this.loadedDbPartnerIds.update(ids => new Set([...ids, saved.id]));
          const realShareNumberings = this.mapBackendShareNumberings(saved);
          for (const sn of realShareNumberings) {
            if (sn.id != null && sn.id > 0) {
              this.loadedDbShareIds.update(ids => new Set([...ids, sn.id]));
            }
          }
          this.socios.update(list => list.map(s =>
            s.id === socioForTable.id
              ? { ...s, id: saved.id, shareNumberings: realShareNumberings, sharesCount: realShareNumberings.reduce((sum: number, sn: any) => sum + (sn.sharesCount ?? 0), 0) || s.sharesCount }
              : s
          ));
          this.buildSociosTableConfig();
        }
      } else if (deferPersistence) {
        // Local-only until operation save
      } else {
        console.warn('Skipping backend save: No clientId available');
      }
    } catch (error) {
      console.error('Error saving client shareholder:', error);
      // Revert optimistic update on error
      if (isEditing) {
        // Can't easily revert without storing the old socio, so just log
      } else {
        this.socios.update(list => list.filter(s => s.id !== socioForTable.id));
        this.buildSociosTableConfig();
      }
    }
  }

  private mapBackendShareNumberings(savedPartner: any): any[] {
    const raw = Array.isArray(savedPartner?.partnerShares)
      ? savedPartner.partnerShares
      : (Array.isArray(savedPartner?.shareNumberings) ? savedPartner.shareNumberings : []);
    return raw.map((sn: any) => ({
      id: sn.id ?? null,
      seriesId: sn.seriesId ?? sn.series?.id ?? sn.socialCapital?.id ?? null,
      seriesName: sn.seriesName || sn.series?.name || sn.socialCapital?.name || '',
      sharesCount: sn.sharesCount ?? sn.sharesNum,
      sharesNumberFrom: sn.sharesNumberFrom ?? sn.shareFrom,
      sharesNumberTo: sn.sharesNumberTo ?? sn.shareTo,
      titulo: sn.titulo || '',
      ownershipPercentage: sn.ownershipPercentage ?? null
    }));
  }

  deleteSocio(index: number, deferPersistence = false): void {
    const socio = this.socios()[index];
    this.socios.update(list => list.filter((_, i) => i !== index));
    this.buildSociosTableConfig();
    if (!deferPersistence && socio?.id && this.isDbPartnerId(socio.id)) {
      this.clientsService.deleteClientShareholder(socio.id).catch(error => {
        console.error('Error deleting shareholder', error);
      });
    }
  }

  async loadBankAccountsForClient(clientId: number): Promise<void> {
    try {
      const accounts = await this.clientsService.getClientBankAccounts(clientId);
      this.bankAccounts.set((accounts || []).filter((acc: any) => acc.endDate === null));
    } catch (error) {
      console.error('[Socio Modal] Error loading bank accounts:', error);
      this.bankAccounts.set([]);
    }
  }

  private async autofillAddressForClient(clientId: number): Promise<void> {
    try {
      const addresses = await this.clientsService.getClientAddresses(clientId);
      const address = addresses && addresses.length > 0 ? addresses[0] : null;

      if (!address) {
        this.socioForm.get('address')?.setValue('');
        return;
      }

      const parts = [address.street, address.number, address.postalCode, address.town, address.province]
        .filter((p: any) => p !== undefined && p !== null && String(p).trim() !== '');

      const fullAddress = parts.length > 0 ? parts.join(', ') : '';
      this.socioForm.get('address')?.setValue(fullAddress);
    } catch (error) {
      console.error('[Socio Modal] Error loading address:', error);
      this.socioForm.get('address')?.setValue('');
    }
  }

  private sharesRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const countCtrl = group.get('sharesCount');
      const fromCtrl = group.get('sharesNumberFrom');
      const toCtrl = group.get('sharesNumberTo');
      if (!countCtrl || !fromCtrl || !toCtrl) return null;

      const count = Number(countCtrl.value ?? NaN);
      const from = Number(fromCtrl.value ?? NaN);
      const to = Number(toCtrl.value ?? NaN);

      if (isNaN(count) || isNaN(from) || isNaN(to)) return null;
      if (to < from) return { rangeInvalid: true };

      const expectedCount = to - from + 1;
      if (count === expectedCount) return null;

      const difference = count - expectedCount;
      return {
        sharesCountMismatch: {
          expectedCount,
          actualCount: count,
          difference
        }
      };
    };
  }
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function collectReferencedSeries(partners: any[]): Array<{
  id: number;
  name: string;
  totalShares: number | null;
  nominalValue: number | null;
  sharesNumberFrom: number | null;
  sharesNumberTo: number | null;
}> {
  const rows: Array<{
    id: number;
    name: string;
    totalShares: number | null;
    nominalValue: number | null;
    sharesNumberFrom: number | null;
    sharesNumberTo: number | null;
  }> = [];
  const seen = new Set<number>();
  for (const partner of partners ?? []) {
    const shares = Array.isArray(partner?.partnerShares)
      ? partner.partnerShares
      : (Array.isArray(partner?.shareNumberings) ? partner.shareNumberings : []);
    for (const share of shares) {
      const capital = share?.socialCapital ?? share?.series;
      const id = Number(capital?.id ?? share?.seriesId);
      if (!Number.isFinite(id) || seen.has(id)) continue;
      seen.add(id);
      rows.push({
        id,
        name: capital?.name || share?.seriesName || `Serie ${id}`,
        totalShares: toNumberOrNull(capital?.totalShares),
        nominalValue: toNumberOrNull(capital?.nominalValue),
        sharesNumberFrom: toNumberOrNull(capital?.sharesNumberFrom),
        sharesNumberTo: toNumberOrNull(capital?.sharesNumberTo),
      });
    }
  }
  return rows;
}
